/** The opening card: the blobs burst out of the middle, then "Your 2026 Wrapped" tumbles in. */

import { useMemo } from "react";

import { Fit, Letters, Scene, Zone, useStageExtra, type BlobSpec } from "../../story/stage";
import { useTimeline } from "../../story/timeline";
import { wr } from "../../theme";
import type { Period } from "../../types";
import { lastDay, shortDate } from "../shared";

const BLOBS: BlobSpec[] = (
  [
    ["blurple", 150, 290, 330, 0.3],
    ["fuchsia", 960, 420, 270, 1.7],
    ["green", 30, 1800, 330, 2.9],
    ["yellow", 1010, 1760, 360, 4.1],
    ["cyan", 1030, 1230, 140, 5.3],
    ["orange", 560, 1880, 120, 6.2],
  ] as const
).map(([colour, x, y, r, seed], i) => ({ colour, x, y, r, seed, wob: 0.22, from: { x: 540, y: 960, delay: 0.1 + i * 0.1 } }));

const YEAR_COLOURS = ["yellow", "fuchsia", "green", "cyan"].map(wr);

/** "1 Nov 2025". */
const dated = (iso: string) => `${shortDate(iso)} ${iso.slice(0, 4)}`;

export function Intro({ name, period }: { name: string; period: Period }) {
  const title = period.title;
  // The title's letters take the four colours in turn, however long it is.
  const colours = [...title.replace(/ /g, "")].map((_, i) => YEAR_COLOURS[i % YEAR_COLOURS.length]);
  // The burst comes out of the title, which stays centred on a taller stage.
  const extra = useStageExtra();
  const blobs = useMemo(
    () => BLOBS.map((b) => ({ ...b, from: b.from && { ...b.from, y: b.from.y + Math.round(extra / 2) } })),
    [extra],
  );

  const root = useTimeline((s) => {
    s.rise(".i-hey", 0.8, { y: 30 });
    s.letters(".i-your", 1.0, { y: 100 });
    s.tl.fromTo(
      s.q(".i-year .ch"),
      { yPercent: -120, autoAlpha: 0, rotation: -14 },
      { yPercent: 0, autoAlpha: 1, rotation: 0, duration: 0.85, stagger: 0.11, ease: "back.out(1.8)" },
      1.3,
    );
    // The four digits drop in one by one: a note each, climbing.
    for (let i = 0; i < 4; i++) s.sound("pop", 1.3 + i * 0.11 + 0.25, 1.1 * (1 + i * 0.12));
    s.letters(".i-wr", 1.85, { y: 100, stagger: 0.04 });
    s.rise(".i-sub", 2.5, { y: 24 });
    s.tl.to(".i-year", { scale: 1.04, transformOrigin: "30% 60%", duration: 0.5, yoyo: true, repeat: 1, ease: "sine.inOut" }, 2.8);
  });

  return (
    <div ref={root} className="contents">
      <Scene blobs={blobs}>
        <Zone at="middle">
          <div className="i-box">
            <Fit size={36} min={24} className="i-hey">
              {name}, this is
            </Fit>
            <div className="sr-only">Your {title} Wrapped</div>
            <div className="i-your disp" aria-hidden>
              <Letters text="Your" />
            </div>
            {/* The title is set by whoever runs the recap, so it may be longer than a year. */}
            <Fit size={450} min={110} className="i-year disp">
              <span aria-hidden>
                <Letters text={title} colours={colours} />
              </span>
            </Fit>
            <div className="i-wr disp" aria-hidden>
              <Letters text="Wrapped" />
            </div>
            <div className="i-sub">
              {dated(period.start)} – {dated(lastDay(period.end))}
            </div>
          </div>
        </Zone>
      </Scene>
    </div>
  );
}
